/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'

import { TableWrapper } from 'components/atoms/TableWrapper'
import Button from 'components/atoms/Button'
import DataTable from 'components/atoms/DataTable'
import { confirm } from 'components/molecules/Confirmation'
import {
  useAddPhysicianGroupOfficeMutation,
  useGetPhysicianGroupAvailableOfficesQuery,
  useGetPhysicianGroupOfficesQuery,
  useRemovePhysicianGroupOfficeMutation,
} from 'store/services/physician'
import { PhysicianGroupOfficeDto } from 'ts-back-end/src/business/physician/dto/physician.dto'

interface IProps {
  physicianGroupId: number
}

type SelectOption = {
  value: string
  label: string
}

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.4rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

const formatOfficeAddress = (office: PhysicianGroupOfficeDto): string =>
  [
    [office.addressStreetLine1, office.addressStreetLine2].filter(Boolean).join(' '),
    office.addressCity,
    [office.addressState, office.addressZip].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')

export const GroupOfficeSelectionSection = ({ physicianGroupId }: IProps) => {
  const [selectedOffice, setSelectedOffice] = useState<SelectOption | null>(null)

  const {
    data: groupOffices,
    isLoading,
    refetch,
  } = useGetPhysicianGroupOfficesQuery({ id: physicianGroupId })
  const { data: availableOffices, isLoading: isLoadingOffices } =
    useGetPhysicianGroupAvailableOfficesQuery({ id: physicianGroupId })

  const [addGroupOffice, { isLoading: isAdding }] = useAddPhysicianGroupOfficeMutation()
  const [removeGroupOffice] = useRemovePhysicianGroupOfficeMutation()

  const availableOptions = useMemo(() => {
    if (!availableOffices) return []
    const selectedUuids = groupOffices?.flatMap((office) => office.addressUuids) || []

    return availableOffices
      .filter((office) => !office.addressUuids.some((uuid) => selectedUuids.includes(uuid)))
      .map((office) => ({
        value: office.addressUuid,
        label: `${formatOfficeAddress(office)} — ${office.physicianNames.join(', ')}`,
      }))
  }, [availableOffices, groupOffices])

  const handleAddOffice = async () => {
    if (!selectedOffice) return

    try {
      await addGroupOffice({
        id: physicianGroupId,
        addressUuid: selectedOffice.value,
      }).unwrap()
      setSelectedOffice(null)
      refetch()
    } catch (error) {
      console.error('Error adding group office:', error)
      toast.error('Failed to add office.')
    }
  }

  const handleRemoveOffice = async (addressUuid: string) => {
    confirm('', {
      yesAction: 'Yes',
      description:
        'Are you sure you want to remove this office from the group? Group users will regain visibility of orders from all locations if no offices remain selected.',
    })
      .then(async () => {
        try {
          await removeGroupOffice({ id: physicianGroupId, addressUuid }).unwrap()
          refetch()
        } catch (error) {
          console.error('Error removing group office:', error)
          toast.error('Failed to remove office.')
        }
      })
      .catch(() => {})
  }

  const tableColumns = [
    {
      accessor: 'address',
      Header: 'Office Address',
      disableSortBy: true,
      Cell: ({ row }) => <div>{formatOfficeAddress(row.original)}</div>,
    },
    {
      accessor: 'physicians',
      Header: 'Physicians',
      disableSortBy: true,
      Cell: ({ row }) => <div>{row.original.physicianNames.join(', ')}</div>,
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => (
        <button
          type="button"
          className="btn btn-outline-danger btn-sm"
          onClick={() => handleRemoveOffice(row.original.addressUuid)}>
          Remove
        </button>
      ),
    },
  ]

  return (
    <div className="form-group textGray mt-3">
      <label className="form-label">Office Selection</label>
      <div className="mb-2 text-muted" css={{ fontSize: '0.85rem' }}>
        Group users only see orders written at the selected offices. When no offices are
        selected, they see all orders of the group physicians.
      </div>
      <div className="row align-items-end mb-3">
        <div className="col-sm-8">
          <ReactSelect
            className="basic-single"
            classNamePrefix="select"
            isClearable
            isSearchable
            isLoading={isLoadingOffices}
            placeholder="Search for a physician office..."
            value={selectedOffice}
            onChange={(option) => setSelectedOffice(option as SelectOption | null)}
            options={availableOptions}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
          />
        </div>
        <div className="col-sm-4">
          <Button
            className="btn btn-primary"
            label="Add Office"
            icon="fa fa-plus"
            disabled={!selectedOffice || isAdding}
            onClick={handleAddOffice}
          />
        </div>
      </div>
      {!!groupOffices?.length && (
        <StyledTableWrapper className="card-body row">
          <DataTable
            tableKey={'physician_group_offices'}
            data={{ items: groupOffices || [] }}
            status={isLoading}
            tableColumns={tableColumns}
            pageSize={10}
            entityName="office"
          />
        </StyledTableWrapper>
      )}
    </div>
  )
}
